import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  LogOut, 
  Users, 
  BarChart3, 
  Wallet,
  ShoppingCart,
  Plus,
  Check,
  X,
  Edit2,
  Trash2,
  AlertCircle,
  ArrowUpRight,
  RotateCcw,
  DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import CreateAccountModal from '../components/CreateAccountModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminDashboard = () => {
  const { user, logout, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('accounts');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [fundedPhaseRequests, setFundedPhaseRequests] = useState([]);
  const [affiliateWithdrawals, setAffiliateWithdrawals] = useState([]);
  
  // Modal states
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedPayout, setSelectedPayout] = useState(null);

  useEffect(() => {
    // Only redirect if we've loaded user data and they're not admin
    if (!authLoading && !isAdmin && user) {
      navigate('/dashboard');
      return;
    }
    
    if (isAdmin) {
      fetchAllData();
    }
  }, [isAdmin, user, authLoading, navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [usersRes, accountsRes, payoutsRes, purchasesRes, fundedRes, affiliateRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`),
        axios.get(`${API_URL}/admin/accounts`),
        axios.get(`${API_URL}/admin/payout-requests`),
        axios.get(`${API_URL}/admin/purchases`),
        axios.get(`${API_URL}/admin/funded-phase-requests`),
        axios.get(`${API_URL}/admin/affiliate-withdrawals`).catch(() => ({ data: [] }))
      ]);
      
      setUsers(usersRes.data);
      setAccounts(accountsRes.data);
      setPayoutRequests(payoutsRes.data);
      setPurchases(purchasesRes.data);
      setFundedPhaseRequests(fundedRes.data);
      setAffiliateWithdrawals(affiliateRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleApprovePayout = async (payoutId) => {
    try {
      await axios.put(`${API_URL}/admin/payout-requests/${payoutId}/approve`);
      await fetchAllData();
      alert('Payout approved successfully');
    } catch (error) {
      alert('Failed to approve payout');
    }
  };

  const handleDeclinePayout = async (payoutId, reason) => {
    try {
      await axios.put(`${API_URL}/admin/payout-requests/${payoutId}/decline`, {
        decline_reason: reason || 'Declined by admin'
      });
      await fetchAllData();
      alert('Payout declined');
    } catch (error) {
      alert('Failed to decline payout');
    }
  };

  const handleResetPayout = async (payoutId) => {
    try {
      await axios.put(`${API_URL}/admin/payout-requests/${payoutId}/reset`);
      await fetchAllData();
      alert('Payout reset to pending');
    } catch (error) {
      alert('Failed to reset payout');
    }
  };

  const handleMarkPaid = async (payoutId) => {
    try {
      await axios.put(`${API_URL}/admin/payout-requests/${payoutId}/mark-paid`);
      await fetchAllData();
      alert('Payout marked as paid');
    } catch (error) {
      alert('Failed to mark payout as paid');
    }
  };

  // Funded Phase Request handlers
  const handleApproveFundedPhase = async (requestId) => {
    try {
      await axios.put(`${API_URL}/admin/funded-phase-requests/${requestId}/approve`);
      await fetchAllData();
      alert('Funded phase approved - Account is now funded');
    } catch (error) {
      alert('Failed to approve funded phase request');
    }
  };

  const handleDeclineFundedPhase = async (requestId, reason) => {
    try {
      await axios.put(`${API_URL}/admin/funded-phase-requests/${requestId}/decline`, {
        decline_reason: reason || 'Declined by admin'
      });
      await fetchAllData();
      alert('Funded phase request declined');
    } catch (error) {
      alert('Failed to decline funded phase request');
    }
  };

  const handleResetFundedPhase = async (requestId) => {
    try {
      await axios.put(`${API_URL}/admin/funded-phase-requests/${requestId}/reset`);
      await fetchAllData();
      alert('Funded phase request reset to pending');
    } catch (error) {
      alert('Failed to reset funded phase request');
    }
  };

  // Affiliate Withdrawal handlers
  const handleApproveAffiliateWithdrawal = async (withdrawalId) => {
    try {
      await axios.put(`${API_URL}/admin/affiliate-withdrawals/${withdrawalId}/approve`);
      await fetchAllData();
      alert('Affiliate withdrawal approved');
    } catch (error) {
      alert('Failed to approve withdrawal');
    }
  };

  const handleDeclineAffiliateWithdrawal = async (withdrawalId, reason) => {
    try {
      await axios.put(`${API_URL}/admin/affiliate-withdrawals/${withdrawalId}/decline`, {
        admin_notes: reason || 'Declined by admin'
      });
      await fetchAllData();
      alert('Affiliate withdrawal declined');
    } catch (error) {
      alert('Failed to decline withdrawal');
    }
  };

  const handleMarkAffiliateWithdrawalPaid = async (withdrawalId) => {
    try {
      await axios.put(`${API_URL}/admin/affiliate-withdrawals/${withdrawalId}/mark-paid`);
      await fetchAllData();
      alert('Affiliate withdrawal marked as paid');
    } catch (error) {
      alert('Failed to mark withdrawal as paid');
    }
  };

  const handleUpdateAccountStatus = async (accountId, newStatus) => {
    try {
      await axios.put(`${API_URL}/admin/accounts/${accountId}`, {
        status: newStatus
      });
      await fetchAllData();
      alert(`Account status updated to ${newStatus}`);
    } catch (error) {
      alert('Failed to update account status');
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!confirm('Are you sure you want to delete this account?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/accounts/${accountId}`);
      await fetchAllData();
      alert('Account deleted successfully');
    } catch (error) {
      alert('Failed to delete account');
    }
  };

  // PnL management
  const [editingPnl, setEditingPnl] = useState(null);
  const [pnlValue, setPnlValue] = useState('');

  const handlePnlEdit = (accountId, currentPnl) => {
    setEditingPnl(accountId);
    setPnlValue(String(currentPnl || 0));
  };

  const handlePnlSave = async (accountId) => {
    const val = parseFloat(pnlValue);
    if (isNaN(val)) {
      alert('Enter a valid number');
      return;
    }
    try {
      await axios.put(`${API_URL}/admin/accounts/${accountId}`, { pnl: val });
      await fetchAllData();
      setEditingPnl(null);
    } catch (error) {
      alert('Failed to update PnL');
    }
  };

  const handlePnlCancel = () => {
    setEditingPnl(null);
    setPnlValue('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
      case 'funded':
      case 'approved':
      case 'paid':
      case 'completed':
      case 'finished':
      case 'confirmed':
        return 'var(--success)';
      case 'pending':
      case 'waiting':
      case 'confirming':
      case 'sending':
        return 'var(--warning)';
      case 'breached':
      case 'declined':
      case 'failed':
      case 'expired':
      case 'refunded':
        return 'var(--error)';
      default:
        return 'var(--text-muted)';
    }
  };

  // Purchase management handlers
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  const handleSyncAllPayments = async () => {
    setSyncingAll(true);
    try {
      const res = await axios.post(`${API_URL}/admin/purchases/sync-all`);
      await fetchAllData();
      alert(res.data.message);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to sync payment statuses');
    } finally {
      setSyncingAll(false);
    }
  };

  const handleSyncPayment = async (purchaseId) => {
    setSyncingId(purchaseId);
    try {
      const res = await axios.post(`${API_URL}/admin/purchases/${purchaseId}/sync-status`);
      await fetchAllData();
      alert(res.data.message);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to sync payment status');
    } finally {
      setSyncingId(null);
    }
  };

  const handleAssignAccountToPurchase = async (purchaseId) => {
    const accountId = prompt('Enter the Trading Account ID to assign:');
    if (!accountId) return;
    
    try {
      await axios.put(`${API_URL}/admin/purchases/${purchaseId}/assign`, {
        account_id: accountId
      });
      await fetchAllData();
      alert('Account assigned successfully');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to assign account');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)'
    }}>
      {/* Show loading while checking auth */}
      {authLoading ? (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid var(--border-primary)',
              borderTop: '4px solid var(--accent-gold)',
              borderRadius: '50%',
              margin: '0 auto 16px',
              animation: 'spin 1s linear infinite'
            }} />
            <p style={{ color: 'var(--text-muted)' }}>Loading admin dashboard...</p>
          </div>
        </div>
      ) : (
        <>
      {/* Header */}
      <header style={{
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border-subtle)',
        padding: '16px 24px'
      }}>
        <div style={{
          maxWidth: '1600px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <h1 style={{
              fontSize: '24px',
              fontWeight: 700,
              color: 'var(--accent-gold)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <img src="/logo.png" alt="Prop Indexa" style={{ height: '35px' }} />
              PROP INDEXA Admin
            </h1>
            <div style={{
              padding: '4px 12px',
              background: 'rgba(14, 165, 233, 0.1)',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--accent-blue)',
              textTransform: 'uppercase'
            }}>
              Administrator
            </div>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              textAlign: 'right',
              display: 'none'
            }} className="hidden md:block">
              <div style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--text-primary)'
              }}>
                {user?.full_name}
              </div>
              <div style={{
                fontSize: '12px',
                color: 'var(--text-muted)'
              }}>
                {user?.email}
              </div>
            </div>

            <Button
              onClick={handleLogout}
              variant="ghost"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: 'var(--text-muted)'
              }}
            >
              <LogOut size={18} />
              <span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div style={{
        maxWidth: '1600px',
        margin: '0 auto',
        padding: '32px 24px'
      }}>
        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '24px',
          marginBottom: '32px'
        }}>
          <div style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: '16px',
            padding: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <Users size={24} color="var(--accent-gold)" />
              <span style={{
                fontSize: '32px',
                fontWeight: 700,
                color: 'var(--accent-gold)'
              }}>
                {users.length}
              </span>
            </div>
            <div style={{
              fontSize: '14px',
              color: 'var(--text-muted)',
              fontWeight: 500
            }}>
              Total Users
            </div>
          </div>

          <div style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: '16px',
            padding: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <BarChart3 size={24} color="var(--accent-blue)" />
              <span style={{
                fontSize: '32px',
                fontWeight: 700,
                color: 'var(--accent-blue)'
              }}>
                {accounts.length}
              </span>
            </div>
            <div style={{
              fontSize: '14px',
              color: 'var(--text-muted)',
              fontWeight: 500
            }}>
              Trading Accounts
            </div>
          </div>

          <div style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: '16px',
            padding: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <Wallet size={24} color="var(--warning)" />
              <span style={{
                fontSize: '32px',
                fontWeight: 700,
                color: 'var(--warning)'
              }}>
                {payoutRequests.filter(p => p.status === 'pending').length}
              </span>
            </div>
            <div style={{
              fontSize: '14px',
              color: 'var(--text-muted)',
              fontWeight: 500
            }}>
              Pending Payouts
            </div>
          </div>

          <div style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: '16px',
            padding: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <ShoppingCart size={24} color="var(--success)" />
              <span style={{
                fontSize: '32px',
                fontWeight: 700,
                color: 'var(--success)'
              }}>
                {purchases.filter(p => ['completed', 'confirmed', 'finished'].includes(p.payment_status)).length}
              </span>
            </div>
            <div style={{
              fontSize: '14px',
              color: 'var(--text-muted)',
              fontWeight: 500
            }}>
              Confirmed Purchases
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '32px',
          borderBottom: '1px solid var(--border-subtle)',
          overflowX: 'auto'
        }}>
          {[
            { id: 'accounts', label: 'Trading Accounts', icon: <BarChart3 size={18} /> },
            { id: 'payouts', label: 'Payout Requests', icon: <Wallet size={18} />, badge: payoutRequests.filter(p => p.status === 'pending').length },
            { id: 'funded-requests', label: 'Funded Phase Requests', icon: <ArrowUpRight size={18} />, badge: fundedPhaseRequests.filter(r => r.status === 'pending').length },
            { id: 'affiliate-withdrawals', label: 'Affiliate Withdrawals', icon: <DollarSign size={18} />, badge: affiliateWithdrawals.filter(w => w.status === 'pending').length },
            { id: 'purchases', label: 'Purchases', icon: <ShoppingCart size={18} /> },
            { id: 'users', label: 'Users', icon: <Users size={18} /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid var(--accent-gold)' : '2px solid transparent',
                color: activeTab === tab.id ? 'var(--accent-gold)' : 'var(--text-muted)',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                position: 'relative'
              }}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {tab.badge > 0 && (
                <span style={{
                  padding: '2px 8px',
                  background: 'var(--warning)',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: 700,
                  color: 'black'
                }}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '64px 24px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid var(--border-primary)',
              borderTop: '4px solid var(--accent-gold)',
              borderRadius: '50%',
              margin: '0 auto 16px',
              animation: 'spin 1s linear infinite'
            }} />
            <p style={{ color: 'var(--text-muted)' }}>Loading admin data...</p>
          </div>
        ) : (
          <>
            {/* Trading Accounts Tab */}
            {activeTab === 'accounts' && (
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '24px'
                }}>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: 'var(--text-primary)'
                  }}>
                    All Trading Accounts
                  </h3>
                  <Button
                    onClick={() => setShowCreateAccountModal(true)}
                    className="btn-primary"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Plus size={18} />
                    Create Account
                  </Button>
                </div>

                <div style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '16px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    overflowX: 'auto'
                  }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse'
                    }}>
                      <thead>
                        <tr style={{
                          borderBottom: '1px solid var(--border-subtle)',
                          background: 'var(--bg-primary)'
                        }}>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            textTransform: 'uppercase'
                          }}>Account</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            textTransform: 'uppercase'
                          }}>User</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            textTransform: 'uppercase'
                          }}>Status</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            textTransform: 'uppercase'
                          }}>Login ID</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            textTransform: 'uppercase'
                          }}>PnL</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            textTransform: 'uppercase'
                          }}>Created</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'right',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            textTransform: 'uppercase'
                          }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accounts.map(account => {
                          const accountUser = users.find(u => u.id === account.user_id);
                          return (
                            <tr key={account.id} style={{
                              borderBottom: '1px solid var(--border-subtle)'
                            }}>
                              <td style={{
                                padding: '16px'
                              }}>
                                <div style={{
                                  fontSize: '15px',
                                  fontWeight: 600,
                                  color: 'var(--text-primary)',
                                  marginBottom: '4px'
                                }}>
                                  ${account.account_size.toLocaleString()}
                                </div>
                                <div style={{
                                  fontSize: '13px',
                                  color: 'var(--text-muted)'
                                }}>
                                  {account.plan_name}
                                </div>
                              </td>
                              <td style={{
                                padding: '16px',
                                fontSize: '14px',
                                color: 'var(--text-secondary)'
                              }}>
                                {accountUser?.email || 'Unknown'}
                              </td>
                              <td style={{
                                padding: '16px'
                              }}>
                                <div style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '4px 12px',
                                  background: `${getStatusColor(account.status)}20`,
                                  borderRadius: '20px',
                                  color: getStatusColor(account.status),
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  textTransform: 'uppercase'
                                }}>
                                  {account.status}
                                </div>
                              </td>
                              <td style={{
                                padding: '16px',
                                fontSize: '14px',
                                color: 'var(--text-secondary)',
                                fontFamily: 'monospace'
                              }}>
                                {account.login_id}
                              </td>
                              <td style={{ padding: '16px' }}>
                                {editingPnl === account.id ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input
                                      data-testid={`pnl-input-${account.id}`}
                                      type="number"
                                      step="0.01"
                                      value={pnlValue}
                                      onChange={(e) => setPnlValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handlePnlSave(account.id);
                                        if (e.key === 'Escape') handlePnlCancel();
                                      }}
                                      autoFocus
                                      style={{
                                        width: '100px',
                                        padding: '4px 8px',
                                        background: 'var(--bg-primary)',
                                        border: '1px solid var(--accent-gold)',
                                        borderRadius: '6px',
                                        color: 'var(--text-primary)',
                                        fontSize: '13px',
                                        fontFamily: 'monospace'
                                      }}
                                    />
                                    <button
                                      data-testid={`pnl-save-${account.id}`}
                                      onClick={() => handlePnlSave(account.id)}
                                      style={{
                                        padding: '4px 8px',
                                        background: 'rgba(16,185,129,0.15)',
                                        border: '1px solid rgba(16,185,129,0.4)',
                                        borderRadius: '4px',
                                        color: 'var(--success)',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        fontWeight: 700
                                      }}
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={handlePnlCancel}
                                      style={{
                                        padding: '4px 8px',
                                        background: 'rgba(239,68,68,0.1)',
                                        border: '1px solid rgba(239,68,68,0.3)',
                                        borderRadius: '4px',
                                        color: 'var(--error)',
                                        cursor: 'pointer',
                                        fontSize: '11px'
                                      }}
                                    >
                                      X
                                    </button>
                                  </div>
                                ) : (
                                  <div
                                    data-testid={`pnl-display-${account.id}`}
                                    onClick={() => handlePnlEdit(account.id, account.pnl)}
                                    style={{
                                      cursor: 'pointer',
                                      fontFamily: 'monospace',
                                      fontSize: '14px',
                                      fontWeight: 700,
                                      color: (account.pnl || 0) >= 0 ? 'var(--success)' : 'var(--error)',
                                      padding: '4px 8px',
                                      borderRadius: '6px',
                                      border: '1px dashed transparent',
                                      transition: 'border-color 0.2s'
                                    }}
                                    title="Click to edit PnL"
                                    onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--accent-gold)'}
                                    onMouseOut={(e) => e.currentTarget.style.borderColor = 'transparent'}
                                  >
                                    {(account.pnl || 0) >= 0 ? '+' : ''}{(account.pnl || 0).toFixed(2)}
                                  </div>
                                )}
                              </td>
                              <td style={{
                                padding: '16px',
                                fontSize: '14px',
                                color: 'var(--text-muted)'
                              }}>
                                {new Date(account.created_at).toLocaleDateString()}
                              </td>
                              <td style={{
                                padding: '16px',
                                textAlign: 'right'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  gap: '8px'
                                }}>
                                  <select
                                    value={account.status}
                                    onChange={(e) => handleUpdateAccountStatus(account.id, e.target.value)}
                                    style={{
                                      padding: '6px 12px',
                                      background: 'var(--bg-primary)',
                                      border: '1px solid var(--border-primary)',
                                      borderRadius: '6px',
                                      color: 'var(--text-primary)',
                                      fontSize: '13px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <option value="active">Active</option>
                                    <option value="funded">Funded</option>
                                    <option value="passed">Passed</option>
                                    <option value="breached">Breached</option>
                                    <option value="closed">Closed</option>
                                  </select>
                                  <button
                                    onClick={() => handleDeleteAccount(account.id)}
                                    style={{
                                      padding: '6px 12px',
                                      background: 'rgba(239, 68, 68, 0.1)',
                                      border: '1px solid rgba(239, 68, 68, 0.3)',
                                      borderRadius: '6px',
                                      color: 'var(--error)',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {accounts.length === 0 && (
                    <div style={{
                      padding: '64px 24px',
                      textAlign: 'center',
                      color: 'var(--text-muted)'
                    }}>
                      No trading accounts yet
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payout Requests Tab */}
            {activeTab === 'payouts' && (
              <div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  marginBottom: '24px'
                }}>
                  All Payout Requests
                </h3>

                <div style={{
                  display: 'grid',
                  gap: '16px'
                }}>
                  {payoutRequests.map(request => {
                    const requestUser = users.find(u => u.id === request.user_id);
                    return (
                      <div
                        key={request.id}
                        style={{
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: '16px',
                          padding: '24px'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          marginBottom: '16px',
                          flexWrap: 'wrap',
                          gap: '16px'
                        }}>
                          <div>
                            <div style={{
                              fontSize: '28px',
                              fontWeight: 700,
                              color: 'var(--accent-gold)',
                              marginBottom: '4px'
                            }}>
                              ${request.amount.toLocaleString()}
                            </div>
                            <div style={{
                              fontSize: '14px',
                              color: 'var(--text-muted)',
                              marginBottom: '8px'
                            }}>
                              From ${request.profit_amount.toLocaleString()} profit • {requestUser?.email}
                            </div>
                            <div style={{
                              fontSize: '13px',
                              color: 'var(--text-muted)'
                            }}>
                              Requested: {new Date(request.requested_at).toLocaleString()}
                            </div>
                          </div>

                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                          }}>
                            <div style={{
                              padding: '8px 16px',
                              background: `${getStatusColor(request.status)}20`,
                              borderRadius: '20px',
                              color: getStatusColor(request.status),
                              fontSize: '13px',
                              fontWeight: 600,
                              textTransform: 'uppercase'
                            }}>
                              {request.status}
                            </div>

                            {request.status === 'pending' && (
                              <div style={{
                                display: 'flex',
                                gap: '8px'
                              }}>
                                <button
                                  onClick={() => handleApprovePayout(request.id)}
                                  style={{
                                    padding: '8px 16px',
                                    background: 'var(--success)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  <Check size={16} />
                                  Approve
                                </button>
                                <button
                                  onClick={() => {
                                    const reason = prompt('Reason for decline:');
                                    if (reason) handleDeclinePayout(request.id, reason);
                                  }}
                                  style={{
                                    padding: '8px 16px',
                                    background: 'var(--error)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  <X size={16} />
                                  Decline
                                </button>
                              </div>
                            )}

                            {request.status === 'approved' && (
                              <div style={{
                                display: 'flex',
                                gap: '8px'
                              }}>
                                <button
                                  onClick={() => handleMarkPaid(request.id)}
                                  style={{
                                    padding: '8px 16px',
                                    background: 'var(--success)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  <Check size={16} />
                                  Mark as Paid
                                </button>
                                <button
                                  onClick={() => handleResetPayout(request.id)}
                                  style={{
                                    padding: '8px 16px',
                                    background: 'var(--warning)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: 'black',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  <RotateCcw size={16} />
                                  Reset
                                </button>
                              </div>
                            )}

                            {(request.status === 'declined' || request.status === 'paid') && (
                              <button
                                onClick={() => handleResetPayout(request.id)}
                                style={{
                                  padding: '8px 16px',
                                  background: 'var(--bg-primary)',
                                  border: '1px solid var(--border-primary)',
                                  borderRadius: '8px',
                                  color: 'var(--text-primary)',
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                <RotateCcw size={16} />
                                Reset
                              </button>
                            )}
                          </div>
                        </div>

                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '16px',
                          padding: '16px',
                          background: 'var(--bg-primary)',
                          borderRadius: '12px',
                          fontSize: '14px'
                        }}>
                          <div>
                            <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>
                              Cryptocurrency
                            </div>
                            <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                              {request.cryptocurrency || 'N/A'}
                            </div>
                          </div>
                          <div>
                            <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>
                              Wallet Address
                            </div>
                            <div style={{ 
                              color: 'var(--text-primary)', 
                              fontWeight: 600,
                              wordBreak: 'break-all',
                              fontSize: '12px',
                              fontFamily: 'monospace'
                            }}>
                              {request.wallet_address || request.payout_details || 'N/A'}
                            </div>
                          </div>
                        </div>

                        {request.decline_reason && (
                          <div style={{
                            marginTop: '12px',
                            padding: '12px',
                            background: 'rgba(239, 68, 68, 0.05)',
                            borderRadius: '8px',
                            fontSize: '13px',
                            color: 'var(--text-secondary)'
                          }}>
                            <strong style={{ color: 'var(--error)' }}>Declined:</strong> {request.decline_reason}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {payoutRequests.length === 0 && (
                  <div style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: '16px',
                    padding: '64px 24px',
                    textAlign: 'center',
                    color: 'var(--text-muted)'
                  }}>
                    No payout requests yet
                  </div>
                )}
              </div>
            )}

            {/* Funded Phase Requests Tab */}
            {activeTab === 'funded-requests' && (
              <div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  marginBottom: '24px'
                }}>
                  Funded Phase Requests (ONESTEP Accounts)
                </h3>

                <div style={{
                  display: 'grid',
                  gap: '16px'
                }}>
                  {fundedPhaseRequests.map(request => (
                    <div
                      key={request.id}
                      style={{
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-subtle)',
                        borderRadius: '16px',
                        padding: '24px'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        marginBottom: '16px',
                        flexWrap: 'wrap',
                        gap: '16px'
                      }}>
                        <div>
                          <div style={{
                            fontSize: '20px',
                            fontWeight: 700,
                            color: 'var(--accent-gold)',
                            marginBottom: '4px'
                          }}>
                            {request.plan_name} - ${request.account_size?.toLocaleString()}
                          </div>
                          <div style={{
                            fontSize: '14px',
                            color: 'var(--text-muted)',
                            marginBottom: '8px'
                          }}>
                            {request.user_name} ({request.user_email})
                          </div>
                          <div style={{
                            fontSize: '13px',
                            color: 'var(--text-muted)'
                          }}>
                            Requested: {request.requested_at ? new Date(request.requested_at).toLocaleString() : 'N/A'}
                          </div>
                        </div>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <div style={{
                            padding: '8px 16px',
                            background: `${getStatusColor(request.status)}20`,
                            borderRadius: '20px',
                            color: getStatusColor(request.status),
                            fontSize: '13px',
                            fontWeight: 600,
                            textTransform: 'uppercase'
                          }}>
                            {request.status}
                          </div>

                          {request.status === 'pending' && (
                            <div style={{
                              display: 'flex',
                              gap: '8px'
                            }}>
                              <button
                                onClick={() => handleApproveFundedPhase(request.id)}
                                style={{
                                  padding: '8px 16px',
                                  background: 'var(--success)',
                                  border: 'none',
                                  borderRadius: '8px',
                                  color: 'white',
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                <Check size={16} />
                                Approve
                              </button>
                              <button
                                onClick={() => {
                                  const reason = prompt('Reason for decline:');
                                  if (reason) handleDeclineFundedPhase(request.id, reason);
                                }}
                                style={{
                                  padding: '8px 16px',
                                  background: 'var(--error)',
                                  border: 'none',
                                  borderRadius: '8px',
                                  color: 'white',
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                <X size={16} />
                                Decline
                              </button>
                              <button
                                onClick={() => handleResetFundedPhase(request.id)}
                                style={{
                                  padding: '8px 16px',
                                  background: 'var(--warning)',
                                  border: 'none',
                                  borderRadius: '8px',
                                  color: 'black',
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                <RotateCcw size={16} />
                                Reset
                              </button>
                            </div>
                          )}

                          {(request.status === 'approved' || request.status === 'declined') && (
                            <button
                              onClick={() => handleResetFundedPhase(request.id)}
                              style={{
                                padding: '8px 16px',
                                background: 'var(--bg-primary)',
                                border: '1px solid var(--border-primary)',
                                borderRadius: '8px',
                                color: 'var(--text-primary)',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              <RotateCcw size={16} />
                              Reset
                            </button>
                          )}
                        </div>
                      </div>

                      {request.notes && (
                        <div style={{
                          marginTop: '12px',
                          padding: '12px',
                          background: 'var(--bg-primary)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: 'var(--text-secondary)'
                        }}>
                          <strong style={{ color: 'var(--text-primary)' }}>User Notes:</strong> {request.notes}
                        </div>
                      )}

                      {request.admin_notes && (
                        <div style={{
                          marginTop: '12px',
                          padding: '12px',
                          background: 'rgba(14, 165, 233, 0.05)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: 'var(--text-secondary)'
                        }}>
                          <strong style={{ color: 'var(--accent-blue)' }}>Admin Notes:</strong> {request.admin_notes}
                        </div>
                      )}

                      {request.decline_reason && (
                        <div style={{
                          marginTop: '12px',
                          padding: '12px',
                          background: 'rgba(239, 68, 68, 0.05)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: 'var(--text-secondary)'
                        }}>
                          <strong style={{ color: 'var(--error)' }}>Declined:</strong> {request.decline_reason}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {fundedPhaseRequests.length === 0 && (
                  <div style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: '16px',
                    padding: '64px 24px',
                    textAlign: 'center',
                    color: 'var(--text-muted)'
                  }}>
                    No funded phase requests yet
                  </div>
                )}
              </div>
            )}

            {/* Affiliate Withdrawals Tab */}
            {activeTab === 'affiliate-withdrawals' && (
              <div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  marginBottom: '24px'
                }}>
                  Affiliate Commission Withdrawals
                </h3>

                <div style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '16px',
                  overflow: 'hidden'
                }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse'
                    }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          {['User', 'Amount', 'Method', 'Details', 'Status', 'Date', 'Actions'].map(header => (
                            <th key={header} style={{
                              padding: '16px',
                              textAlign: 'left',
                              fontSize: '12px',
                              fontWeight: 600,
                              color: 'var(--text-muted)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {affiliateWithdrawals.map(withdrawal => (
                          <tr key={withdrawal.id} style={{
                            borderBottom: '1px solid var(--border-subtle)'
                          }}>
                            <td style={{ padding: '16px' }}>
                              <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                                {withdrawal.user_name}
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                {withdrawal.user_email}
                              </div>
                            </td>
                            <td style={{ padding: '16px' }}>
                              <span style={{ fontWeight: 600, color: 'var(--accent-gold)' }}>
                                ${withdrawal.amount.toFixed(2)}
                              </span>
                            </td>
                            <td style={{ padding: '16px' }}>
                              <span style={{
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 500,
                                background: withdrawal.payment_method === 'crypto' 
                                  ? 'rgba(212, 175, 55, 0.1)' 
                                  : 'rgba(14, 165, 233, 0.1)',
                                color: withdrawal.payment_method === 'crypto' 
                                  ? 'var(--accent-gold)' 
                                  : 'var(--accent-blue)',
                                textTransform: 'capitalize'
                              }}>
                                {withdrawal.payment_method}
                              </span>
                            </td>
                            <td style={{ padding: '16px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                              {withdrawal.payment_method === 'crypto' ? (
                                <>
                                  <div>{withdrawal.cryptocurrency}</div>
                                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', wordBreak: 'break-all', maxWidth: '200px' }}>
                                    {withdrawal.wallet_address}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div>{withdrawal.bank_name}</div>
                                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                                    {withdrawal.account_name} • {withdrawal.account_number}
                                  </div>
                                </>
                              )}
                            </td>
                            <td style={{ padding: '16px' }}>
                              <span style={{
                                padding: '6px 12px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: 600,
                                background: withdrawal.status === 'paid' ? 'rgba(34, 197, 94, 0.1)' :
                                           withdrawal.status === 'approved' ? 'rgba(14, 165, 233, 0.1)' :
                                           withdrawal.status === 'declined' ? 'rgba(239, 68, 68, 0.1)' :
                                           'rgba(245, 158, 11, 0.1)',
                                color: withdrawal.status === 'paid' ? 'var(--success)' :
                                       withdrawal.status === 'approved' ? 'var(--accent-blue)' :
                                       withdrawal.status === 'declined' ? 'var(--error)' :
                                       'var(--warning)',
                                textTransform: 'capitalize'
                              }}>
                                {withdrawal.status}
                              </span>
                            </td>
                            <td style={{ padding: '16px', fontSize: '13px', color: 'var(--text-muted)' }}>
                              {new Date(withdrawal.requested_at).toLocaleDateString()}
                            </td>
                            <td style={{ padding: '16px' }}>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                {withdrawal.status === 'pending' && (
                                  <>
                                    <button
                                      onClick={() => handleApproveAffiliateWithdrawal(withdrawal.id)}
                                      style={{
                                        padding: '6px 12px',
                                        background: 'var(--success)',
                                        border: 'none',
                                        borderRadius: '6px',
                                        color: 'white',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        cursor: 'pointer'
                                      }}
                                    >
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => {
                                        const reason = prompt('Enter reason for declining:');
                                        if (reason) handleDeclineAffiliateWithdrawal(withdrawal.id, reason);
                                      }}
                                      style={{
                                        padding: '6px 12px',
                                        background: 'var(--error)',
                                        border: 'none',
                                        borderRadius: '6px',
                                        color: 'white',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        cursor: 'pointer'
                                      }}
                                    >
                                      Decline
                                    </button>
                                  </>
                                )}
                                {withdrawal.status === 'approved' && (
                                  <button
                                    onClick={() => handleMarkAffiliateWithdrawalPaid(withdrawal.id)}
                                    style={{
                                      padding: '6px 12px',
                                      background: 'var(--accent-gold)',
                                      border: 'none',
                                      borderRadius: '6px',
                                      color: 'black',
                                      fontSize: '12px',
                                      fontWeight: 500,
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Mark Paid
                                  </button>
                                )}
                                {(withdrawal.status === 'paid' || withdrawal.status === 'declined') && (
                                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                    Completed
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {affiliateWithdrawals.length === 0 && (
                    <div style={{
                      textAlign: 'center',
                      padding: '48px',
                      color: 'var(--text-muted)'
                    }}>
                      No affiliate withdrawal requests yet
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Purchases & Users tabs continue below... */}

            {/* Purchases Tab */}
            {activeTab === 'purchases' && (
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '24px',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: 'var(--text-primary)'
                  }}>
                    All Purchases
                    <span style={{
                      fontSize: '12px',
                      fontWeight: 400,
                      color: 'var(--text-muted)',
                      marginLeft: '12px'
                    }}>
                      Auto-syncs with NowPayments every 2 min
                    </span>
                  </h3>
                  <Button
                    data-testid="sync-all-payments-btn"
                    onClick={handleSyncAllPayments}
                    disabled={syncingAll}
                    className="btn-primary"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      fontSize: '13px'
                    }}
                  >
                    <RotateCcw size={14} className={syncingAll ? 'animate-spin' : ''} />
                    {syncingAll ? 'Syncing...' : 'Sync All from NowPayments'}
                  </Button>
                </div>

                <div style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '16px',
                  overflow: 'hidden'
                }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse'
                    }}>
                      <thead>
                        <tr style={{
                          borderBottom: '1px solid var(--border-subtle)',
                          background: 'var(--bg-primary)'
                        }}>
                          {['User', 'Plan', 'Amount', 'Payment Status', 'Account Assigned', 'Date', 'Actions'].map(header => (
                            <th key={header} style={{
                              padding: '16px',
                              textAlign: 'left',
                              fontSize: '13px',
                              fontWeight: 600,
                              color: 'var(--text-muted)',
                              textTransform: 'uppercase'
                            }}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {purchases.map(purchase => {
                          const purchaseUser = users.find(u => u.id === purchase.user_id);
                          return (
                            <tr key={purchase.id} style={{
                              borderBottom: '1px solid var(--border-subtle)'
                            }}>
                              <td style={{
                                padding: '16px',
                                fontSize: '14px',
                                color: 'var(--text-secondary)'
                              }}>
                                {purchaseUser?.email || 'Unknown'}
                              </td>
                              <td style={{
                                padding: '16px',
                                fontSize: '14px',
                                color: 'var(--text-primary)',
                                fontWeight: 600
                              }}>
                                {purchase.plan_type.toUpperCase()} ${purchase.account_size.toLocaleString()}
                              </td>
                              <td style={{
                                padding: '16px',
                                fontSize: '14px',
                                color: 'var(--accent-gold)',
                                fontWeight: 600
                              }}>
                                ${purchase.amount_paid}
                              </td>
                              <td style={{ padding: '16px' }}>
                                <div style={{
                                  display: 'inline-flex',
                                  padding: '4px 12px',
                                  background: `${getStatusColor(purchase.payment_status)}15`,
                                  border: `1px solid ${getStatusColor(purchase.payment_status)}40`,
                                  borderRadius: '20px',
                                  color: getStatusColor(purchase.payment_status),
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  textTransform: 'uppercase'
                                }}>
                                  {purchase.payment_status}
                                </div>
                              </td>
                              <td style={{ padding: '16px' }}>
                                <div style={{
                                  display: 'inline-flex',
                                  padding: '4px 12px',
                                  background: purchase.account_assigned ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                  borderRadius: '20px',
                                  color: purchase.account_assigned ? 'var(--success)' : 'var(--warning)',
                                  fontSize: '12px',
                                  fontWeight: 600
                                }}>
                                  {purchase.account_assigned ? 'YES' : 'PENDING'}
                                </div>
                              </td>
                              <td style={{
                                padding: '16px',
                                fontSize: '14px',
                                color: 'var(--text-muted)'
                              }}>
                                {new Date(purchase.purchased_at).toLocaleDateString()}
                              </td>
                              <td style={{ padding: '16px' }}>
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  <button
                                    data-testid={`sync-payment-btn-${purchase.id}`}
                                    onClick={() => handleSyncPayment(purchase.id)}
                                    disabled={syncingId === purchase.id}
                                    title="Sync status from NowPayments"
                                    style={{
                                      padding: '6px 10px',
                                      background: 'rgba(14, 165, 233, 0.1)',
                                      border: '1px solid rgba(14, 165, 233, 0.3)',
                                      borderRadius: '8px',
                                      color: 'var(--accent-blue)',
                                      cursor: 'pointer',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                  >
                                    <RotateCcw size={12} className={syncingId === purchase.id ? 'animate-spin' : ''} />
                                    Sync
                                  </button>
                                  {!purchase.account_assigned && (
                                    <button
                                      data-testid={`assign-account-btn-${purchase.id}`}
                                      onClick={() => handleAssignAccountToPurchase(purchase.id)}
                                      title="Assign trading account"
                                      style={{
                                        padding: '6px 10px',
                                        background: 'rgba(16, 185, 129, 0.1)',
                                        border: '1px solid rgba(16, 185, 129, 0.3)',
                                        borderRadius: '8px',
                                        color: 'var(--success)',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                    >
                                      <Plus size={12} />
                                      Assign
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {purchases.length === 0 && (
                    <div style={{
                      padding: '64px 24px',
                      textAlign: 'center',
                      color: 'var(--text-muted)'
                    }}>
                      No purchases yet
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  marginBottom: '24px'
                }}>
                  All Users
                </h3>

                <div style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '16px',
                  overflow: 'hidden'
                }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse'
                    }}>
                      <thead>
                        <tr style={{
                          borderBottom: '1px solid var(--border-subtle)',
                          background: 'var(--bg-primary)'
                        }}>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            textTransform: 'uppercase'
                          }}>Name</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            textTransform: 'uppercase'
                          }}>Email</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            textTransform: 'uppercase'
                          }}>Phone</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            textTransform: 'uppercase'
                          }}>Role</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            textTransform: 'uppercase'
                          }}>Status</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            textTransform: 'uppercase'
                          }}>Registered</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(user => (
                          <tr key={user.id} style={{
                            borderBottom: '1px solid var(--border-subtle)'
                          }}>
                            <td style={{
                              padding: '16px',
                              fontSize: '14px',
                              color: 'var(--text-primary)',
                              fontWeight: 600
                            }}>
                              {user.full_name}
                            </td>
                            <td style={{
                              padding: '16px',
                              fontSize: '14px',
                              color: 'var(--text-secondary)'
                            }}>
                              {user.email}
                            </td>
                            <td style={{
                              padding: '16px',
                              fontSize: '14px',
                              color: 'var(--text-muted)'
                            }}>
                              {user.phone || 'N/A'}
                            </td>
                            <td style={{
                              padding: '16px'
                            }}>
                              <div style={{
                                display: 'inline-flex',
                                padding: '4px 12px',
                                background: user.role === 'admin' ? 'rgba(14, 165, 233, 0.1)' : 'rgba(212, 175, 55, 0.1)',
                                borderRadius: '20px',
                                color: user.role === 'admin' ? 'var(--accent-blue)' : 'var(--accent-gold)',
                                fontSize: '12px',
                                fontWeight: 600,
                                textTransform: 'uppercase'
                              }}>
                                {user.role}
                              </div>
                            </td>
                            <td style={{
                              padding: '16px'
                            }}>
                              <div style={{
                                display: 'inline-flex',
                                padding: '4px 12px',
                                background: `${getStatusColor(user.status)}20`,
                                borderRadius: '20px',
                                color: getStatusColor(user.status),
                                fontSize: '12px',
                                fontWeight: 600,
                                textTransform: 'uppercase'
                              }}>
                                {user.status}
                              </div>
                            </td>
                            <td style={{
                              padding: '16px',
                              fontSize: '14px',
                              color: 'var(--text-muted)'
                            }}>
                              {new Date(user.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {users.length === 0 && (
                    <div style={{
                      padding: '64px 24px',
                      textAlign: 'center',
                      color: 'var(--text-muted)'
                    }}>
                      No users yet
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Create Account Modal */}
        <CreateAccountModal
          isOpen={showCreateAccountModal}
          onClose={() => setShowCreateAccountModal(false)}
          onSuccess={fetchAllData}
          users={users}
        />
      </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
